import { type Theme } from '@mui/material/styles';
import { type TextFieldProps } from '@mui/material/TextField';
import { type SxProps } from '@mui/system';
import {
    DesktopDatePicker,
    LocalizationProvider,
    MobileDatePicker,
    type PickerChangeHandlerContext
} from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { type DateView } from '@mui/x-date-pickers/models';
import React, {
    type FocusEvent,
    type KeyboardEvent,
    memo,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import AIAdornment from '../../components/AIAdornment';
import { useBrowserOrAccountLocale } from '../../hooks/useBrowserOrAccountLocale';
import { getL10nDef } from '../../i18n/localization';
import { useStyling } from '../../Theme';
import { getAIAdornmentStylingForDatePicker } from '../../utils/commonUtils';
import {
    compareCommittedDateValues,
    constructCommittedDateValue as constructCommittedDateValueFromInput,
    constructPickerDateValue,
    type DeferredDateValidationError,
    verifyIsDeferredDateEditingKey
} from './dateFormWidgetUtils/dateFormWidgetUtils';

type DateFormWidgetProps = {
    /** Label displayed above the date picker input. */
    label?: ReactNode;
    /** Disables all date picker interaction. */
    disabled?: boolean;
    /** Makes the field non-editable while keeping it focusable. */
    readOnly?: boolean;
    /** Restricts which date sections are shown in the picker. */
    views?: DateView[];
    /** Provides the base date MUI uses when constructing incomplete values. */
    referenceDate?: Date;
    /** Disables specific dates in the calendar. */
    shouldDisableDate?: (date: Date) => boolean;
    /** Current committed field value from form state. */
    value?: string | Date | null;
    /** Called when the widget commits a new date value. */
    onChange: (newValue: string | Date | null) => void;
    /** Controls the MUI text field size. */
    size?: TextFieldProps['size'];
    /** Marks the field as being in an error state. */
    isError?: boolean;
    /** Helper or validation text shown below the field. */
    errorText?: string;
    /** Marks the field as required in the UI. */
    required?: boolean;
    /** Preferred locale used to localize date parsing and display. */
    locale?: string;
    /** Indicates that the field value came from AI extraction. */
    isAIExtractedData?: boolean;
    /** Resolves the AI extracted state after a user-confirmed change. */
    onResolveAIExtractedValue?: () => void;
    /** Defers keyboard-entered date commits until the field loses focus. */
    deferKeyboardDateCommitUntilBlur?: boolean;
    /** Minimum selectable date. */
    minDate?: Date;
    /** Maximum selectable date. */
    maxDate?: Date;
    /** Custom styles applied to the rendered field. */
    fieldSx?: SxProps<Theme>;
};

/**
 * Renders the shared form date picker used by `FormWidget`.
 *
 * It supports the standard date-field flow and the booking-specific deferred
 * keyboard commit mode, where typed date edits stay local until the user leaves
 * the field while calendar selections still commit immediately.
 */
function DateFormWidget({
    label,
    disabled = false,
    readOnly = false,
    views,
    referenceDate,
    shouldDisableDate,
    value,
    onChange,
    size,
    isError = false,
    errorText = '',
    required = false,
    locale,
    isAIExtractedData = false,
    onResolveAIExtractedValue,
    deferKeyboardDateCommitUntilBlur = false,
    minDate,
    maxDate,
    fieldSx
}: DateFormWidgetProps) {
    const { isMobile } = useStyling();
    const resolvedLocale = useBrowserOrAccountLocale() || locale;
    const l10n = getL10nDef(resolvedLocale);
    const PickerComponent = isMobile ? MobileDatePicker : DesktopDatePicker;
    const [deferredDateValue, setDeferredDateValue] = useState<Date | null>(null);
    const [hasDeferredDateValue, setHasDeferredDateValue] = useState(false);
    const deferredDateValidationErrorRef = useRef<DeferredDateValidationError>(null);
    const deferredDateCommitValueRef = useRef<string | Date | null | undefined>(undefined);
    const isDeferredKeyboardDateEntryRef = useRef(false);
    const isDatePickerOpenRef = useRef(false);

    const cleanupDeferredDateState = useCallback(() => {
        setDeferredDateValue(null);
        setHasDeferredDateValue(false);
        deferredDateValidationErrorRef.current = null;
        deferredDateCommitValueRef.current = undefined;
    }, []);

    const resolveAIExtractedValue = useCallback(() => {
        if (!isAIExtractedData) {
            return;
        }

        onResolveAIExtractedValue?.();
    }, [isAIExtractedData, onResolveAIExtractedValue]);

    const constructResolvedDateValue = useCallback(
        (newValue: Date | null, validationError: PickerChangeHandlerContext<string>['validationError']) =>
            constructCommittedDateValueFromInput(newValue, validationError, value),
        [value]
    );

    const commitDateValue = useCallback(
        (
            newValue: Date | null,
            validationError: PickerChangeHandlerContext<string>['validationError'],
            preserveDeferredPickerValue = false
        ) => {
            const nextCommittedValue = constructResolvedDateValue(newValue, validationError);

            if (preserveDeferredPickerValue) {
                setDeferredDateValue(newValue);
                setHasDeferredDateValue(true);
                deferredDateCommitValueRef.current = nextCommittedValue;
            } else {
                cleanupDeferredDateState();
            }

            if (compareCommittedDateValues(value, nextCommittedValue)) {
                cleanupDeferredDateState();
                resolveAIExtractedValue();
                return;
            }

            onChange(nextCommittedValue);
            resolveAIExtractedValue();
        },
        [cleanupDeferredDateState, constructResolvedDateValue, onChange, resolveAIExtractedValue, value]
    );

    const pickerValue = useMemo(() => {
        return constructPickerDateValue({
            deferKeyboardDateCommitUntilBlur,
            hasDeferredDateValue,
            deferredDateValue,
            value
        });
    }, [deferKeyboardDateCommitUntilBlur, deferredDateValue, hasDeferredDateValue, value]);

    const handleDatePickerOpen = useCallback(() => {
        isDatePickerOpenRef.current = true;
        isDeferredKeyboardDateEntryRef.current = false;
    }, []);

    const handleDatePickerClose = useCallback(() => {
        isDatePickerOpenRef.current = false;
    }, []);

    const handleDatePickerChange = useCallback(
        (newValue: Date | null, context: PickerChangeHandlerContext<string>) => {
            if (deferKeyboardDateCommitUntilBlur && isDeferredKeyboardDateEntryRef.current) {
                setDeferredDateValue(newValue);
                setHasDeferredDateValue(true);
                deferredDateValidationErrorRef.current = context.validationError;
                return;
            }

            commitDateValue(newValue, context.validationError, deferKeyboardDateCommitUntilBlur);
        },
        [commitDateValue, deferKeyboardDateCommitUntilBlur]
    );

    const handleTextFieldBlur = useCallback(
        (event: FocusEvent<HTMLElement>) => {
            const nextFocusedElement = event.relatedTarget;

            if (nextFocusedElement instanceof Node && event.currentTarget.contains(nextFocusedElement)) {
                return;
            }

            if (isDatePickerOpenRef.current) {
                return;
            }

            isDeferredKeyboardDateEntryRef.current = false;

            if (!hasDeferredDateValue) {
                return;
            }

            commitDateValue(
                deferredDateValue,
                deferredDateValidationErrorRef.current,
                !compareCommittedDateValues(
                    value,
                    constructResolvedDateValue(deferredDateValue, deferredDateValidationErrorRef.current)
                )
            );
        },
        [commitDateValue, constructResolvedDateValue, deferredDateValue, hasDeferredDateValue, value]
    );

    const handleTextFieldKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
        if (verifyIsDeferredDateEditingKey(event)) {
            isDeferredKeyboardDateEntryRef.current = true;
        }
    }, []);

    const handleTextFieldPaste = useCallback(() => {
        isDeferredKeyboardDateEntryRef.current = true;
    }, []);

    const textFieldSlotProps = useMemo(() => {
        if (!deferKeyboardDateCommitUntilBlur) {
            return undefined;
        }

        const dateTextFieldProps = {
            onBlur: handleTextFieldBlur,
            onKeyDown: handleTextFieldKeyDown,
            onPaste: handleTextFieldPaste
        };

        return dateTextFieldProps;
    }, [deferKeyboardDateCommitUntilBlur, handleTextFieldBlur, handleTextFieldKeyDown, handleTextFieldPaste]);

    const slotProps = useMemo(() => {
        return {
            textField: {
                size,
                fullWidth: true,
                error: isError,
                helperText: errorText,
                required,
                sx: {
                    ...fieldSx,
                    ...getAIAdornmentStylingForDatePicker()
                },
                InputProps: {
                    startAdornment: isAIExtractedData && <AIAdornment />
                },
                ...(textFieldSlotProps ?? {})
            }
        };
    }, [errorText, fieldSx, isAIExtractedData, isError, required, size, textFieldSlotProps]);

    useEffect(() => {
        if (
            typeof deferredDateCommitValueRef.current !== 'undefined' &&
            compareCommittedDateValues(value, deferredDateCommitValueRef.current)
        ) {
            cleanupDeferredDateState();
        }
    }, [cleanupDeferredDateState, value]);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={l10n.dateFns}>
            <PickerComponent
                label={label}
                disabled={disabled}
                readOnly={readOnly}
                views={views}
                referenceDate={referenceDate}
                shouldDisableDate={shouldDisableDate}
                value={pickerValue}
                onOpen={handleDatePickerOpen}
                onClose={handleDatePickerClose}
                onChange={handleDatePickerChange}
                sx={fieldSx}
                slotProps={slotProps}
                minDate={minDate}
                maxDate={maxDate}
            />
        </LocalizationProvider>
    );
}

const MemoizedDateFormWidget = memo(DateFormWidget);
MemoizedDateFormWidget.displayName = 'DateFormWidget';

export default MemoizedDateFormWidget;
